/**
 * The conference roster — who else is in the room, live — and the moderator commands a rep
 * sends about the people on it.
 *
 * ## How it arrives
 *
 * FreeSWITCH does not send the member list with the call. When a member joins a conference whose
 * profile carries the `livearray-sync` flag, mod_conference pushes a private message down the
 * verto session:
 *
 *     { action: "conference-liveArray-join", laChannel, modChannel, laName, role,
 *       conferenceMemberID }
 *
 * The vendored client surfaces that as `pvtEvent`. `laChannel` is an event channel the client
 * then has to **subscribe** to, and then **ask** on: the subscription brings `add` / `modify` /
 * `del` as people come, change and go from that moment, and `bootObj` (the whole list as it
 * stands) only in reply to a `bootstrap` command broadcast on the same channel, which the
 * softphone context sends once subscribed. Nothing arrives unasked — a client that never
 * subscribes sits in a populated room seeing nobody, and one that subscribes without asking
 * sees only whoever speaks after it arrived.
 *
 * Both halves have to be in place. The flags live in `fs-xml-gateway`'s conference profile
 * (`src/defaults.py`), and are conference *flags*, not params.
 *
 * ## The wire format
 *
 * There is no specification for it — the Verto docs say so outright — so everything here is
 * defensive, and the tests are the record of what real FreeSWITCH builds actually emit. A
 * participant row is positional:
 *
 *     [memberId, orderId, name, codec, jsonStatus, …]
 *
 * and `bootObj` rows wrap it as either `[hashKey, payload]` or `[hashKey, orderId, payload]`
 * depending on the build. `jsonStatus` is a JSON *string* when `livearray-json-status` is set
 * and an object on older builds. Guessing wrong on any of these produces a roster of blanks
 * rather than an error, so each shape is parsed explicitly and covered by a test.
 *
 * Commands go the other way, on a different channel, and are documented where they are built —
 * see "Acting on somebody else in the room" at the foot of this file.
 *
 * Adapted from tap-ucaas `web/lib/softphone/conference.ts`. Kept as pure functions over a
 * plain state object rather than that repo's class-with-listeners: the console already has a
 * place for state (the softphone context), and pure reducers are what the wire shapes above
 * need in order to be testable at all.
 */

import type { LegKind, RoomLeg } from "@/lib/types";

export interface ConferenceParticipant {
  /** The key the liveArray addresses this row by — what `modify` and `del` carry. */
  hashKey: string;
  /**
   * This member's channel uuid, which is what the hash key above IS: mod_conference keys its
   * liveArray by `switch_core_session_get_uuid(member->session)`. It is read back out under its
   * own name because that is the only reason the key is interesting — it is the id core-api
   * identifies the leg by — and null when the key is not uuid-shaped, so a FreeSWITCH build that
   * keys the array some other way costs the badges and nothing else.
   */
  channelId: string | null;
  /** Member id assigned by FreeSWITCH; what a moderator command would name. */
  memberId: string;
  name: string;
  muted: boolean;
  /**
   * Whether the switch is holding the room's audio back from this member.
   *
   * Read for the same reason `muted` is: a moderator control that toggles a flag has to know
   * which way it is currently set, and mod_conference reports this one — `audio.deaf` — right
   * beside the mute flag in the same status object. Absent from the pre-JSON status string, in
   * which case it reads false, exactly as `muted` does.
   */
  deaf: boolean;
  /**
   * The switch is holding this member: muted, and hearing the hold media instead of the room.
   * `audio.onHold` in the same status object, reported from the channel's own hold flag, which
   * `conference <room> hold` sets. On a direct call (ADR-032) it is how the phone knows the
   * caller is on hold, faster than the server's picture of the room.
   */
  onHold: boolean;
  /** Currently speaking. */
  talking: boolean;
  /** Holds the conference floor (the dominant speaker). */
  floor: boolean;
  isSelf: boolean;
  /**
   * What this leg is, once the server has said. Null until then, and after a lookup that could
   * not answer — see `applyLegKinds`. None of it comes from the switch's roster: mod_conference
   * describes a member of a conference, not a leg of a call.
   */
  kind: LegKind | null;
  direction: "inbound" | "outbound" | null;
}

export interface ConferenceState {
  /** Conference name, e.g. `1001-room`. */
  name: string;
  /** The event channel the roster arrives on; also the unsubscribe handle. */
  channel: string;
  /**
   * The event channel moderator commands are broadcast on, or null when this phone may not
   * issue them.
   *
   * Null is the switch's answer, not a default of ours: mod_conference puts `modChannel` in the
   * join message only for a member carrying the moderator flag, so its presence *is* the
   * permission. `role` below carries the same bit, but this is the one the commands need, and
   * gating on the thing you are about to use leaves no room for the two to disagree.
   */
  modChannel: string | null;
  role: "moderator" | "participant";
  /** Our own member id, from the join message. */
  selfMemberId: string | null;
  /**
   * The leg that is *in* the room.
   *
   * Not part of the roster — it is the console's own bookkeeping, and the reason it is kept is
   * that "move this caller into my room" must know which call is the room itself. Transferring
   * the room leg's partner would be a no-op (a conference leg has no bridge partner), so
   * getting this wrong degrades to a button that does nothing rather than to a dropped call —
   * but a button that does nothing is still worth not offering.
   */
  callId: string | null;
  participants: ConferenceParticipant[];
  /**
   * The rows left out of `participants` for good: a supervisor listening in on a campaign call
   * (ADR-041), by hash key. Kept so that the `modify` every talking change sends for them does not
   * put them back, and forgotten when they leave. Absent is none.
   */
  hidden?: readonly string[];
}

/** The private message mod_conference pushes when we join or leave a room. */
export interface ConferencePvtData {
  action?: string;
  laChannel?: string;
  modChannel?: string;
  laName?: string;
  role?: string;
  conferenceMemberID?: string | number;
  chatChannel?: string;
}

const JOIN = "conference-liveArray-join";
const PART = "conference-liveArray-part";

/** Pull the pvtData out of a `pvtEvent`. One object per event, as everywhere else here. */
export function pvtDataFrom(args: unknown[]): ConferencePvtData | null {
  const payload = args[0];
  if (!payload || typeof payload !== "object") return null;
  const pvt = (payload as { pvtData?: unknown }).pvtData;
  if (!pvt || typeof pvt !== "object") return null;
  return pvt as ConferencePvtData;
}

export function isConferencePart(pvt: ConferencePvtData): boolean {
  return pvt.action === PART;
}

/**
 * The empty roster to start from when we join, or null when this pvtData is not a conference
 * join. Screen-share and video-only sub-events ride the same message, hence the discriminator.
 */
export function conferenceFromJoin(
  pvt: ConferencePvtData,
  callId: string | null = null,
): ConferenceState | null {
  if (pvt.action !== JOIN || !pvt.laChannel) return null;
  return {
    name: pvt.laName ?? "Conference",
    channel: pvt.laChannel,
    modChannel: typeof pvt.modChannel === "string" && pvt.modChannel ? pvt.modChannel : null,
    role: pvt.role === "moderator" ? "moderator" : "participant",
    selfMemberId: pvt.conferenceMemberID != null ? String(pvt.conferenceMemberID) : null,
    callId,
    participants: [],
  };
}

/**
 * The dialog this pvtEvent belongs to.
 *
 * `callID` rides on the event's params, one level above `pvtData` — mod_verto addresses the
 * message to the leg that joined. It is read defensively and separately from the join itself
 * because no specification pins it: a build that omits it gives us null, and the caller falls
 * back to the leg it knows it just answered rather than losing the roster over a missing field.
 */
export function conferenceCallIdFrom(args: unknown[]): string | null {
  const payload = args[0];
  if (!payload || typeof payload !== "object") return null;
  const callId = (payload as { callID?: unknown }).callID;
  return typeof callId === "string" && callId ? callId : null;
}

/** One liveArray packet off the subscribed channel. */
export interface LiveArrayPacket {
  action?: string;
  name?: string;
  hashKey?: string | number;
  data?: unknown;
}

/**
 * Fold a liveArray packet into the roster. Returns the same object when nothing changed, so a
 * React setState can skip the re-render.
 */
export function applyLiveArrayEvent(
  state: ConferenceState,
  packet: LiveArrayPacket,
): ConferenceState {
  // A packet for a different conference is not ours to apply. Defensive: one call means one
  // room, but a stale subscription outliving a room would otherwise corrupt the next one.
  if (packet.name && state.name && packet.name !== state.name) return state;

  switch (packet.action) {
    case "bootObj": {
      const rows = Array.isArray(packet.data) ? packet.data : [];
      const hidden = new Set(state.hidden ?? []);
      const participants: ConferenceParticipant[] = [];
      for (const row of rows) {
        if (!Array.isArray(row) || row.length < 2) continue;
        // `[hashKey, payload]` on some builds, `[hashKey, orderId, payload]` on others. The
        // payload is whichever trailing element is an array.
        const last = row[row.length - 1];
        const payload = Array.isArray(last) ? last : row[1];
        const key = String(row[0]);
        if (hidden.has(key) || isMonitorRow(payload)) {
          hidden.add(key);
          continue;
        }
        participants.push(toParticipant(state, key, payload, existing(state, key)));
      }
      return withHidden({ ...state, participants }, hidden);
    }
    case "add":
    case "modify": {
      if (packet.hashKey == null) return state;
      // `data` is the positional row, except when it is that row wrapped in an array.
      const payload =
        Array.isArray(packet.data) && Array.isArray(packet.data[0]) ? packet.data[0] : packet.data;
      const key = String(packet.hashKey);
      if (state.hidden?.includes(key) || isMonitorRow(payload)) return hide(state, [key]);
      // `modify` fires every time somebody starts or stops talking, and rebuilds the row from
      // the packet. Without carrying the identification over, a rep's badges would blink out
      // the moment anyone spoke, and the console would ask the server for them all over again.
      const next = toParticipant(state, key, payload, existing(state, key));
      const index = state.participants.findIndex((p) => p.hashKey === next.hashKey);
      const participants =
        index === -1
          ? [...state.participants, next]
          : state.participants.map((p, i) => (i === index ? next : p));
      return { ...state, participants };
    }
    case "del": {
      if (packet.hashKey == null) return state;
      const key = String(packet.hashKey);
      if (state.hidden?.includes(key)) {
        return withHidden(state, new Set(state.hidden.filter((k) => k !== key)));
      }
      const participants = state.participants.filter((p) => p.hashKey !== key);
      return participants.length === state.participants.length ? state : { ...state, participants };
    }
    case "clear":
      return state.participants.length === 0 ? state : { ...state, participants: [] };
    default:
      return state;
  }
}

/**
 * Whether a liveArray row is a supervisor listening in (ADR-041).
 *
 * mod_conference copies each `verto_dvar_<key>` channel variable of a member into the row's sixth
 * column when they join, prefix stripped, and the ACD sets `verto_dvar_acl_leg=monitor` on the
 * supervisor's leg. So the row says it the moment it arrives, before any lookup could.
 */
function isMonitorRow(payload: unknown): boolean {
  if (!Array.isArray(payload)) return false;
  const dvars = payload[5];
  return (
    dvars !== null &&
    typeof dvars === "object" &&
    (dvars as Record<string, unknown>).acl_leg === "monitor"
  );
}

/** Leave these rows out of the roster from now on. */
function hide(state: ConferenceState, keys: readonly string[]): ConferenceState {
  const hidden = new Set(state.hidden ?? []);
  keys.forEach((k) => hidden.add(k));
  const participants = state.participants.filter((p) => !hidden.has(p.hashKey));
  if (participants.length === state.participants.length && hidden.size === (state.hidden?.length ?? 0)) {
    return state;
  }
  return withHidden({ ...state, participants }, hidden);
}

function withHidden(state: ConferenceState, hidden: ReadonlySet<string>): ConferenceState {
  if (hidden.size === 0 && state.hidden === undefined) return state;
  return { ...state, hidden: [...hidden] };
}

function existing(state: ConferenceState, hashKey: string): ConferenceParticipant | undefined {
  return state.participants.find((p) => p.hashKey === hashKey);
}

function toParticipant(
  state: ConferenceState,
  hashKey: string,
  payload: unknown,
  previous?: ConferenceParticipant,
): ConferenceParticipant {
  let memberId = hashKey;
  let name = hashKey;
  let status: MemberStatus = {};

  if (Array.isArray(payload)) {
    if (payload[0] != null) memberId = String(payload[0]);
    if (typeof payload[2] === "string" && payload[2]) name = payload[2];
    status = parseStatus(payload[4]);
  } else if (payload && typeof payload === "object") {
    // The documented object shape. Rare in the wild, kept so a build that sends it degrades to
    // a correct roster rather than a column of hash keys.
    const p = payload as {
      participantId?: string | number;
      memberID?: string | number;
      participantName?: string;
      participantNumber?: string;
      audio?: MemberStatus["audio"];
      video?: MemberStatus["video"];
    };
    const id = p.participantId ?? p.memberID;
    if (id != null) memberId = String(id);
    name = p.participantName || p.participantNumber || name;
    status = { audio: p.audio, video: p.video };
  }

  // FreeSWITCH writes names as `Display Name <1001>`; the angle-bracket half is noise next to a
  // list of people.
  name = name.replace(/\s*<[^>]+>\s*$/, "").trim() || memberId;

  return {
    hashKey,
    channelId: UUID_RE.test(hashKey) ? hashKey.toLowerCase() : null,
    memberId,
    name,
    muted: Boolean(status.audio?.muted),
    deaf: Boolean(status.audio?.deaf),
    onHold: Boolean(status.audio?.onHold),
    talking: Boolean(status.audio?.talking),
    floor: Boolean(status.audio?.floor),
    isSelf: isSelf(memberId, state.selfMemberId),
    kind: previous?.kind ?? null,
    direction: previous?.direction ?? null,
  };
}

/** Uuid-shaped, in the one form FreeSWITCH writes: lowercase hex with dashes. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a row is us.
 *
 * Compared with leading zeros stripped because builds disagree about whether the member id in
 * the join message is padded: `conferenceMemberID: 7` against a row carrying `"0007"` is the
 * same person, and marking neither row as self puts the rep's own name in the list as a
 * stranger.
 */
function isSelf(memberId: string, selfMemberId: string | null): boolean {
  if (!selfMemberId) return false;
  if (memberId === selfMemberId) return true;
  const strip = (s: string) => s.replace(/^0+/, "") || "0";
  return strip(memberId) === strip(selfMemberId);
}

interface MemberStatus {
  audio?: { muted?: boolean; deaf?: boolean; onHold?: boolean; talking?: boolean; floor?: boolean };
  video?: { muted?: boolean };
}

/**
 * The status column: a JSON string when the profile sets `livearray-json-status`, an object on
 * builds that predate it, and absent when the flag is missing entirely — in which case everyone
 * simply reads as unmuted and silent rather than the parse throwing.
 */
function parseStatus(raw: unknown): MemberStatus {
  if (raw == null) return {};
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed.startsWith("{")) return {};
    try {
      return JSON.parse(trimmed) as MemberStatus;
    } catch {
      return {};
    }
  }
  if (typeof raw === "object") return raw as MemberStatus;
  return {};
}

/**
 * The channel uuids in this roster that nothing has identified yet.
 *
 * The console asks core-api about exactly these, and the shape of that exchange is what keeps it
 * from looping: a leg the server cannot place comes back as `unknown`, which is still an answer
 * and takes the row out of this list. A leg the server does not answer for at all — a lookup
 * that failed, an index that is down — stays in it, and the effect that watches this list only
 * re-runs when the list itself changes, so nothing is retried until the roster does something.
 */
export function unidentifiedChannelIds(state: ConferenceState): string[] {
  return state.participants
    .filter((p) => p.kind === null && p.channelId !== null)
    .map((p) => p.channelId as string);
}

/**
 * Fold core-api's answer into the roster. Returns the same object when nothing changed.
 *
 * Rows the answer does not mention are left alone rather than cleared: the reply covers the legs
 * the live-channel index still knew about, and "not mentioned" means "no news", not "no longer a
 * lead". An answer that arrives after its subject left the room matches nothing and is dropped,
 * which is why this needs no cancellation on the caller's side.
 */
export function applyLegKinds(state: ConferenceState, legs: readonly RoomLeg[]): ConferenceState {
  if (legs.length === 0) return state;
  // A supervisor the row itself did not name (a leg dialled before the ACD marked them): the
  // server's word takes the row out, and keeps it out (ADR-041).
  const monitors = new Set(
    legs.filter((leg) => leg.kind === "monitor").map((leg) => leg.channel_id.toLowerCase()),
  );
  const kept =
    monitors.size === 0
      ? state
      : hide(
          state,
          state.participants
            .filter((p) => p.channelId !== null && monitors.has(p.channelId))
            .map((p) => p.hashKey),
        );
  const byChannel = new Map(legs.map((leg) => [leg.channel_id.toLowerCase(), leg]));
  let changed = false;
  const participants = kept.participants.map((p) => {
    const leg = p.channelId ? byChannel.get(p.channelId) : undefined;
    if (!leg || (leg.kind === p.kind && (leg.direction ?? null) === p.direction)) return p;
    changed = true;
    return { ...p, kind: leg.kind, direction: leg.direction ?? null };
  });
  return changed ? { ...kept, participants } : kept;
}

const KIND_LABELS: Record<LegKind, string | null> = {
  lead: "Lead",
  caller: "Caller",
  rep: "Rep",
  room: "Room line",
  // Never on screen: the phone drops a supervisor's row (`applyLegKinds`).
  monitor: null,
  // Identified as un-identifiable. A row reading "Unknown" tells a rep less than a bare row
  // does, so this one is deliberately nothing.
  unknown: null,
};

/**
 * How a roster row is labelled, or null for a row with nothing to say.
 *
 * Kind and direction are two different questions — who is on the leg, and which way the call
 * went — and both matter on a warm transfer: "Lead · outbound" is a lead the dialer reached,
 * "Lead · inbound" is one who called back, and a rep treats those differently. They are joined
 * here rather than in the markup so that the rule is testable and there is one of it.
 */
export function describeLeg(participant: ConferenceParticipant): string | null {
  if (!participant.kind) return null;
  const label = KIND_LABELS[participant.kind];
  if (!label) return null;
  // Their own line is the rep's own leg into their own room. "Room line · outbound" would
  // describe the switch ringing their handset, which is not a fact about anybody in the room.
  if (!participant.direction || participant.kind === "room") return label;
  return `${label} · ${participant.direction}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Moderator commands
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ## Acting on somebody else in the room
 *
 * The roster arrives on one event channel; commands go out on another. A moderator command is
 * an ordinary `verto.broadcast` onto `modChannel` carrying an `application: "conf-control"`
 * envelope, which mod_conference's `conference_event_mod_channel_handler` turns into a
 * `conference <name> <cmd> <member>` API call.
 *
 * Four things about that handler decide the shape of everything below, and each of them fails
 * silently rather than loudly when you get it wrong — which is why they are written down here
 * instead of discovered twice:
 *
 * 1. **The command vocabulary is a fixed list.** The handler matches `command` against a
 *    hard-coded set and, on no match, broadcasts `{"error": "Invalid Command"}` back and does
 *    nothing else. It is *not* a passthrough to the conference API, so a command being valid at
 *    `fs_cli` says nothing about whether it is valid here — see `hup` in `MEMBER_COMMANDS`.
 * 2. **The member id is read as a number first.** The handler tries `id.valueint` and only then
 *    `id.valuestring`. The roster's own member id is zero-padded to four digits (`"0007"`),
 *    because mod_conference builds that column with `%0.4d`, so it is parsed here rather than
 *    forwarded as-is.
 * 3. **`value` is one string or an array of them**, and nothing else — a number or an object is
 *    ignored, leaving the command to run with an argument missing.
 * 4. **Every command is answered on the same channel**, success or failure, as an `action:
 *    "response"` packet. That answer is the only place a refusal shows up: the broadcast itself
 *    succeeds regardless, so a console that does not read it cannot tell a command the switch
 *    carried out from one it threw away. See `modResponseFrom`.
 *
 * Being a moderator is not checked here beyond `modChannel` being present, which is the switch's
 * own answer to that question (see `ConferenceState.modChannel`).
 */
export type MemberAction = "mute" | "unmute" | "deaf" | "undeaf" | "remove" | "transfer";

/**
 * The conf-control command each action sends.
 *
 * `mute`/`unmute` are sent explicitly rather than as the `tmute` toggle mod_conference also
 * offers: the roster already says which way the flag is set, so a toggle would only add the
 * chance of inverting it against a stale row. Same for `deaf`/`undeaf`.
 *
 * `remove` is `kick`, not `hup`. Both exist in the conference API and both end the member's leg,
 * but only `kick` is in the mod-channel handler's list — `hup` reaches it and comes back
 * "Invalid Command", which is exactly the kind of silent nothing this file exists to avoid.
 */
const MEMBER_COMMANDS: Record<MemberAction, string> = {
  mute: "mute",
  unmute: "unmute",
  deaf: "deaf",
  undeaf: "undeaf",
  remove: "kick",
  transfer: "transfer",
};

/**
 * What each action is called where a rep can see it — on the menu item that sends it, and as the
 * subject of the sentence when the switch refuses it. One list so those two never drift.
 */
export const MEMBER_ACTION_LABELS: Record<MemberAction, string> = {
  mute: "Mute",
  unmute: "Unmute",
  // Not "deafen": the word for it in every conferencing product a rep has used is the state, and
  // the tooltip on the control says what it does.
  deaf: "Deafen",
  undeaf: "Undeafen",
  remove: "Remove from room",
  transfer: "Transfer",
};

/** The `data` of a `verto.broadcast` that carries a moderator command. */
export interface ConfControlPayload {
  application: "conf-control";
  command: string;
  id: number;
  /** Omitted entirely when the command takes no argument — see note 3 above. */
  value?: string;
}

/**
 * The payload for one action against one member, or null when it cannot be built.
 *
 * Null for a member id that is not a number, and for a transfer with nothing to transfer to.
 * Both would otherwise reach the switch as a command with a missing or zero id, which
 * mod_conference runs against whatever member that resolves to rather than rejecting.
 */
export function memberCommand(
  action: MemberAction,
  participant: Pick<ConferenceParticipant, "memberId">,
  value?: string,
): ConfControlPayload | null {
  const id = Number.parseInt(participant.memberId, 10);
  if (!Number.isFinite(id) || id <= 0) return null;

  // The destination of a transfer is dialled in the member's own context: the handler passes it
  // to `switch_ivr_session_transfer` with the dialplan and context left empty, which means "keep
  // this channel's". So a bare extension or number is the whole argument, and naming a context
  // from the console would be inventing routing the switch already knows.
  const argument = action === "transfer" ? value?.trim() : undefined;
  if (action === "transfer" && !argument) return null;

  const payload: ConfControlPayload = {
    application: "conf-control",
    command: MEMBER_COMMANDS[action],
    id,
  };
  if (argument) payload.value = argument;
  return payload;
}

/**
 * Whether a row's own microphone is off, which on the rep's own row is not the switch's business.
 *
 * A rep's Mute is their phone's: `toggleMute` disables the local audio track, because that — and
 * not the `verto.modify` sent after it, best effort — is what actually stops the microphone
 * reaching the call. mod_conference is never told, so the liveArray goes on reporting the rep's
 * own member as unmuted, and the row beside a lit Mute tile said the room could hear them.
 *
 * So the rep's own row reads the phone as well as the liveArray: the row answers "can the room
 * hear me", and on that row the phone is the one that knows. It is still an `or` — a host who
 * muted this rep in the room muted them, whatever their phone thinks. Every other row is the
 * switch's word alone, which is the only word there is about somebody else's microphone.
 */
export function rowMuted(participant: ConferenceParticipant, phoneMuted: boolean): boolean {
  return participant.muted || (participant.isSelf && phoneMuted);
}

/**
 * Whether an action is worth offering for a row in its current state.
 *
 * A member on hold in the room (`held`) is offered no mute or deaf either way: the hold has their
 * mic and their ears, and on a campaign call the ACD may have deafened a caller so they cannot
 * hear a briefing (ADR-041). Hold brings them back. The roster's own `onHold` does not show a
 * hold done in the room, so the screen passes the server's word as well.
 */
export function memberActionApplies(
  action: MemberAction,
  participant: ConferenceParticipant,
  held = participant.onHold,
): boolean {
  switch (action) {
    case "mute":
      return !held && !participant.muted;
    case "unmute":
      return !held && participant.muted;
    case "deaf":
      return !held && !participant.deaf;
    case "undeaf":
      return !held && participant.deaf;
    // A rep's own row is not offered these two anywhere in the UI, but the rule belongs with the
    // others rather than only in the markup: "remove me from my own room" and "transfer me out
    // of it" are both the rep hanging up, which the dock already does and says so.
    case "remove":
    case "transfer":
      return !participant.isSelf;
  }
}

/** What the switch said about a command, once it answers on the moderator channel. */
export interface ModResponse {
  /** The command as the switch echoed it, e.g. `1001-room kick 7`, or null when it never got that far. */
  command: string | null;
  ok: boolean;
  /** The switch's own words. Worth showing verbatim — it is the only account of what went wrong. */
  message: string;
}

/**
 * Read one packet off the moderator channel, or null if it is not a command response.
 *
 * Responses are not correlated to requests — no id comes back — but they do not need to be: the
 * switch echoes the API call it built in `conf-command`, so a failure says which command failed
 * without the console having to remember what it asked for.
 *
 * A refusal arrives in one of two forms. `error` is the handler declining before it ran anything
 * ("Invalid Command"). Otherwise `response` is the conference API's own output, which reports
 * failure as a `-ERR` prefix and success as `+OK` — and, for a few commands, as neither, so
 * anything that is not `-ERR` counts as carried out.
 */
export function modResponseFrom(packet: unknown): ModResponse | null {
  if (!packet || typeof packet !== "object") return null;
  const data = packet as { action?: unknown; error?: unknown; response?: unknown } & Record<
    string,
    unknown
  >;
  if (data.action !== "response") return null;

  const command = typeof data["conf-command"] === "string" ? data["conf-command"] : null;
  if (typeof data.error === "string" && data.error) {
    return { command, ok: false, message: data.error };
  }
  const response = typeof data.response === "string" ? data.response.trim() : "";
  if (/^-ERR/i.test(response)) {
    return { command, ok: false, message: response.replace(/^-ERR\s*/i, "") || "Refused." };
  }
  return { command, ok: true, message: response };
}

/**
 * The sentence to show a rep for a command the switch refused.
 *
 * The switch's own message is kept — it is specific in a way nothing written here could be — but
 * it is written for `fs_cli`, so it is introduced by what the rep actually did.
 */
export function describeModFailure(response: ModResponse, action: string): string {
  const detail = response.message.trim();
  return detail ? `${action} failed: ${detail}` : `${action} failed.`;
}
